import { type FacilityStaff } from "@packages/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { type ReactNode, useMemo, useRef } from "react";
import { type Merge } from "type-fest";
import { useTranslate } from "../../services";
import { css, cx } from "../../styled-system/css";
import { TableStyle, TypographyStyle, skeletonStyle } from "../common/index";

const classes = TableStyle.raw({ foot: false });

export type FacilityStaffTableProperties = Merge<
  React.ComponentPropsWithoutRef<"div">,
  {
    action?: { (facilityStaff: FacilityStaff): ReactNode };
    columns?: Readonly<Partial<Record<"action" | "name" | "roles" | "tier" | "userKey", boolean>>>;
    loading?: boolean;
    rows?: ReadonlyArray<FacilityStaff>;
  }
>;

export function FacilityStaffTable({
  action,
  className,
  columns,
  loading,
  rows = [],
  ...properties
}: FacilityStaffTableProperties) {
  const translate = useTranslate();

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize() {
      return 64;
    },
    getScrollElement() {
      return parentRef.current;
    },
    overscan: 20,
  });

  const gridTemplateColumns = useMemo(
    function () {
      return [
        columns?.userKey !== false ? "104px" : "",
        columns?.name !== false ? "minmax(120px, 1fr)" : "",
        columns?.tier !== false ? "80px" : "",
        columns?.roles !== false ? "120px" : "",
        columns?.action !== false ? "64px" : "",
      ]
        .filter(function (column) {
          return column !== "";
        })
        .join(" ");
    },
    [columns],
  );

  return (
    <div {...properties} className={cx(css(classes.root), className)}>
      <div className={css(classes.head)}>
        <div className={css(classes.headRow)} style={{ gridTemplateColumns }}>
          {columns?.userKey !== false && <div>{translate("%UserKey.0%")}</div>}
          {columns?.name !== false && <div>{translate("%Name.0%")}</div>}
          {columns?.tier !== false && <div>{translate("%FacilityStaffTier.0%")}</div>}
          {columns?.roles !== false && <div>{translate("%FacilityStaffRole.0%")}</div>}
          {columns?.action !== false && <div />}
        </div>
      </div>

      <div className={css(classes.virtualOuterBody)} ref={parentRef}>
        <div
          className={css(classes.virtualInnerBody)}
          style={{ height: virtualizer.getTotalSize() + "px" }}
        >
          {virtualizer.getVirtualItems().map(function (virtualItem) {
            const facilityStaff = rows[virtualItem.index];
            return (
              <div
                className={css(classes.virtualBodyRow)}
                data-index={virtualItem.index}
                key={virtualItem.key}
                ref={virtualizer.measureElement}
                style={{
                  gridTemplateColumns,
                  height: virtualItem.size + "px",
                  transform: "translateY(" + virtualItem.start + "px)",
                }}
              >
                {columns?.userKey !== false && (
                  <div
                    className={TypographyStyle({
                      ellipsis: true,
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: 1,
                      monospace: true,
                    })}
                  >
                    {facilityStaff.userKey}
                  </div>
                )}
                {columns?.name !== false && (
                  <div
                    className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}
                  >
                    {facilityStaff.name}
                  </div>
                )}
                {columns?.tier !== false && (
                  <div>{translate("%FacilityStaffTier." + facilityStaff.tier + ".0%")}</div>
                )}
                {columns?.roles !== false && (
                  <div className={css({ display: "flex", flexWrap: "wrap", gap: "4px" })}>
                    {facilityStaff.roles.map(function (facilityStaffRole) {
                      return (
                        <span
                          className={css(
                            TypographyStyle.raw({
                              ellipsis: true,
                              fontSize: "14px",
                              lineHeight: 1,
                            }),
                            {
                              backgroundColor: "muted-background",
                            },
                          )}
                          key={facilityStaffRole}
                        >
                          {translate("%FacilityStaffRole." + facilityStaffRole + ".1%")}
                        </span>
                      );
                    })}
                  </div>
                )}
                {columns?.action !== false && <div>{action?.(facilityStaff)}</div>}
              </div>
            );
          })}
        </div>

        {rows.length === 0 &&
          (loading ? (
            Array.from({ length: 3 }).map(function (_, index) {
              return (
                <div
                  className={css(classes.bodyRow, { padding: "16px" })}
                  key={index}
                  style={{ gridTemplateColumns }}
                >
                  <div className={css(skeletonStyle, { maxWidth: "96px" })} />
                  <div className={css(skeletonStyle, { maxWidth: "128px" })} />
                  <div />
                </div>
              );
            })
          ) : (
            <div className={css(classes.bodyRow, { padding: "16px" })}>
              <p className={TypographyStyle({ fontWeight: 500 })}>{translate("%NoItems.0%")}</p>
            </div>
          ))}
      </div>
    </div>
  );
}
